import { genericMemo } from "@redotech/react-util/component";
import { MenuItem, Section } from "@redotech/redo-model/email-builder";
import Quill from "quill";
import { memo, useEffect, useRef, useState } from "react";
import { FormQuillEditor } from "../../form-builder/widget-editors/form-quill-editor";
import { FormQuillEditorToolbar } from "../../form-builder/widget-editors/form-quill-editor-toolbar";
import {
  WidgetEditorProps,
  WidgetProps,
} from "../../form-builder/widget-editors/widget-editor-decorator";
import { WidgetElementEditor } from "../../form-builder/widget-editors/widget-element-editor";
import { convertToQuillHTML } from "../../quill/quill-editor";

export const MenuEditor = genericMemo(function MenuEditor<
  T extends WidgetProps<Section.Menu>,
>(props: WidgetEditorProps<T, Section.Menu>) {
  const menuSelected = props.selected;
  const { element } = props.widget;

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    undefined,
  );

  return (
    <WidgetElementEditor {...props} skipRenderingSectionToolbar>
      {(props) => (
        <>
          <div
            style={{
              position: "relative",
              display: "flex",
              backgroundColor: element.sectionColor,
              padding: `${element.sectionPadding.top}px ${element.sectionPadding.right}px ${element.sectionPadding.bottom}px ${element.sectionPadding.left}px`,
            }}
          >
            {element.menuItems.map((item, index) => {
              return (
                <MenuItemEditor
                  element={element}
                  index={index}
                  item={item}
                  key={index}
                  menuSelected={menuSelected}
                  selectedIndex={selectedIndex}
                  setItem={(item) => {
                    const newMenuItems = [...element.menuItems];
                    newMenuItems[index] = item;
                    props.setElement({ ...element, menuItems: newMenuItems });
                  }}
                  setSelectedIndex={setSelectedIndex}
                />
              );
            })}
          </div>
          {props.sectionToolbar}
        </>
      )}
    </WidgetElementEditor>
  );
});

const MenuItemEditor = memo(function MenuItemEditor({
  item,
  setItem,
  index,
  element,
  menuSelected,
  selectedIndex,
  setSelectedIndex,
}: {
  item: MenuItem;
  setItem: (item: MenuItem) => void;
  index: number;
  element: Section.Menu;
  menuSelected: boolean;
  selectedIndex: number | undefined;
  setSelectedIndex: (index: number | undefined) => void;
}) {
  const containerId = `menu-container-${element.blockId}-${index}`;
  const toolbarId = `menu-toolbar-${element.blockId}-${index}`;

  const quillRef = useRef<Quill | null>(null);

  useEffect(() => {
    if (
      quillRef.current &&
      item.label &&
      quillRef.current.getSemanticHTML() !== item.label
    ) {
      quillRef.current.root.innerHTML = convertToQuillHTML(item.label);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quillRef.current, item.label]);

  return (
    <div
      key={index}
      onClick={() => setSelectedIndex(index)}
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        width: `${100 / element.menuItems.length}%`,
      }}
    >
      <FormQuillEditorToolbar
        dynamicVariables={[]}
        id={toolbarId}
        open={selectedIndex === index && menuSelected}
      />
      <style>
        {`#${containerId} a {
          color: ${element.linkColor};
        }
        #${containerId} div {padding: 0px;}`}
      </style>
      <div
        id={containerId}
        style={{
          color: element.textColor,
          fontSize: element.fontSize,
          fontFamily: element.fontFamily,
          border: "none",
          backgroundColor: element.sectionColor,
        }}
      >
        <FormQuillEditor
          onTextChange={(text) => {
            if (!quillRef.current) {
              return;
            }
            const html = quillRef.current.getSemanticHTML();
            if (item.label === html) {
              return;
            }
            const newMenuItems = [...element.menuItems];
            newMenuItems[index] = { ...item, label: html };
            setItem({ ...item, label: html });
          }}
          placeholder="Menu item..."
          ref={quillRef}
          selector={containerId}
          toolbarElementId={toolbarId}
        />
      </div>
    </div>
  );
});
